import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface DemoData {
  companyName: string;
  companyInfo: {
    industry: string;
    employees: string;
    founded: string;
    headquarters: string;
  };
  metrics: {
    activeTasks: number;
    completedTasks: number;
    activeAgents: number;
    successRate: string;
  };
  isDemo: boolean;
}

interface DemoContextType {
  demoData: DemoData;
  enableDemoMode: () => void;
  disableDemoMode: () => void;
  resetDemoData: () => void;
  isDemoEnabled: boolean;
}

const defaultDemoData: DemoData = {
  companyName: 'TechFlow Dynamics',
  companyInfo: {
    industry: 'Financial Technology',
    employees: '150-200',
    founded: '2019',
    headquarters: 'Austin, TX'
  },
  metrics: {
    activeTasks: 24,
    completedTasks: 156,
    activeAgents: 8,
    successRate: '94.2%'
  },
  isDemo: false
};

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [demoData, setDemoData] = useState<DemoData>(defaultDemoData);
  const { toast } = useToast();

  useEffect(() => {
    // Check if demo mode is enabled in localStorage
    const isDemoEnabled = localStorage.getItem('demo-mode') === 'true';
    if (isDemoEnabled) {
      setDemoData(prev => ({ ...prev, isDemo: true }));
    }
  }, []);

  const enableDemoMode = () => {
    setDemoData(prev => ({ ...prev, isDemo: true }));
    localStorage.setItem('demo-mode', 'true');
    toast({
      title: "Demo Mode Enabled",
      description: `Welcome to ${demoData.companyName} demo environment!`,
    });
  };

  const disableDemoMode = () => {
    setDemoData(prev => ({ ...prev, isDemo: false }));
    localStorage.removeItem('demo-mode');
    toast({
      title: "Demo Mode Disabled",
      description: "Returned to normal mode",
    });
  };

  const resetDemoData = () => {
    setDemoData(defaultDemoData);
    localStorage.removeItem('demo-mode');
    toast({
      title: "Demo Data Reset",
      description: "All demo data has been reset to default values",
    });
  };

  return (
    <DemoContext.Provider value={{
      demoData,
      enableDemoMode,
      disableDemoMode,
      resetDemoData,
      isDemoEnabled: demoData.isDemo
    }}>
      {children}
    </DemoContext.Provider>
  );
};

export const useDemoMode = () => {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoProvider');
  }
  return context;
};